import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from "react";
import AppSectionCard from "./AppSectionCard";
import { pageStyles } from "../styles/appTheme";
const API_BASE = import.meta.env.VITE_API_BASE || "";
function getCookie(name) {
    if (typeof document === "undefined")
        return "";
    const safe = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const match = document.cookie.match(new RegExp(`(?:^|; )${safe}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : "";
}
function getToken() {
    const keys = [
        "ava_session_token",
        "ava_token",
        "token",
        "access_token",
    ];
    for (const key of keys) {
        const value = localStorage.getItem(key) ||
            sessionStorage.getItem(key) ||
            getCookie(key);
        if (value)
            return value;
    }
    return "";
}
function formatBytes(value) {
    let amount = Math.max(0, Number(value || 0));
    if (!Number.isFinite(amount) || amount === 0) {
        return "0 B";
    }
    const units = ["B", "KB", "MB", "GB"];
    let unit = 0;
    while (amount >= 1024 && unit < units.length - 1) {
        amount /= 1024;
        unit += 1;
    }
    const precision = amount >= 10 || unit === 0 ? 0 : 1;
    return `${amount.toFixed(precision)} ${units[unit]}`;
}
function detailFrom(data, fallback) {
    if (typeof data?.detail === "string")
        return data.detail;
    if (typeof data?.message === "string")
        return data.message;
    return fallback;
}
export default function LocalLibrarianPanel({ isElevated, }) {
    const token = useMemo(() => getToken(), []);
    const headers = useMemo(() => ({
        "Content-Type": "application/json",
        ...(token
            ? {
                Authorization: `Bearer ${token}`,
            }
            : {}),
    }), [token]);
    const [status, setStatus] = useState(null);
    const [sources, setSources] = useState([]);
    const [recommendations, setRecommendations] = useState([]);
    const [gaps, setGaps] = useState([]);
    const [rankingsByGapId, setRankingsByGapId] = useState({});
    const [gapTitle, setGapTitle] = useState("");
    const [gapDescription, setGapDescription] = useState("");
    const [gapRequiredTerms, setGapRequiredTerms] = useState("");
    const [gapPreferredTerms, setGapPreferredTerms] = useState("");
    const [gapAllowedLicenses, setGapAllowedLicenses] = useState("");
    const [gapMaxSizeMb, setGapMaxSizeMb] = useState("");
    const [gapPriority, setGapPriority] = useState("normal");
    const [sourceName, setSourceName] = useState("");
    const [publisher, setPublisher] = useState("");
    const [baseUrl, setBaseUrl] = useState("");
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState("");
    const [warning, setWarning] = useState("");
    async function safeJson(response) {
        return response.json().catch(() => ({}));
    }
    async function loadLibrarian() {
        if (!isElevated)
            return;
        setWarning("");
        try {
            const results = await Promise.allSettled([
                fetch(`${API_BASE}/api/v1/admin/librarian/status`, {
                    credentials: "include",
                    headers,
                }),
                fetch(`${API_BASE}/api/v1/admin/librarian/sources`, {
                    credentials: "include",
                    headers,
                }),
                fetch(`${API_BASE}/api/v1/admin/librarian/recommendations`, {
                    credentials: "include",
                    headers,
                }),
                fetch(`${API_BASE}/api/v1/admin/librarian/gaps`, {
                    credentials: "include",
                    headers,
                }),
            ]);
            const warnings = [];
            if (results[0].status === "fulfilled") {
                const data = await safeJson(results[0].value);
                if (results[0].value.ok) {
                    setStatus(data);
                }
                else {
                    warnings.push(detailFrom(data, "Unable to load Librarian status."));
                }
            }
            else {
                warnings.push("Librarian status request failed.");
            }
            if (results[1].status === "fulfilled") {
                const data = await safeJson(results[1].value);
                if (results[1].value.ok) {
                    setSources(Array.isArray(data?.sources) ? data.sources : []);
                }
                else {
                    warnings.push(detailFrom(data, "Unable to load approved sources."));
                }
            }
            else {
                warnings.push("Approved sources request failed.");
            }
            if (results[2].status === "fulfilled") {
                const data = await safeJson(results[2].value);
                if (results[2].value.ok) {
                    setRecommendations(Array.isArray(data?.recommendations)
                        ? data.recommendations
                        : []);
                }
                else {
                    warnings.push(detailFrom(data, "Unable to load knowledge recommendations."));
                }
            }
            else {
                warnings.push("Recommendations request failed.");
            }
            if (results[3].status === "fulfilled") {
                const data = await safeJson(results[3].value);
                if (results[3].value.ok) {
                    setGaps(Array.isArray(data?.gaps) ? data.gaps : []);
                }
                else {
                    warnings.push(detailFrom(data, "Unable to load capability gaps."));
                }
            }
            else {
                warnings.push("Capability gaps request failed.");
            }
            setWarning(warnings.join(" "));
        }
        catch (error) {
            setWarning(error?.message || "Local Librarian request failed.");
        }
    }
    useEffect(() => {
        if (!isElevated) {
            setStatus(null);
            setSources([]);
            setRecommendations([]);
            setGaps([]);
            setRankingsByGapId({});
            setMessage("");
            setWarning("");
            return;
        }
        void loadLibrarian();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isElevated]);
    async function mutate(path, body, successFallback) {
        try {
            setBusy(true);
            setMessage("");
            setWarning("");
            const response = await fetch(`${API_BASE}${path}`, {
                method: "POST",
                credentials: "include",
                headers,
                body: JSON.stringify(body),
            });
            const data = await safeJson(response);
            if (!response.ok) {
                setMessage(`❌ ${detailFrom(data, "Librarian request failed.")}`);
                return false;
            }
            setMessage(data?.message || successFallback);
            await loadLibrarian();
            return true;
        }
        catch (error) {
            setMessage(error?.message || "Librarian operation failed.");
            return false;
        }
        finally {
            setBusy(false);
        }
    }
    function parseList(value) {
        return Array.from(new Set(value
            .split(",")
            .map((item) => item.trim())
            .filter(Boolean)));
    }
    async function createGap() {
        const title = gapTitle.trim();
        const description = gapDescription.trim();
        const requiredTerms = parseList(gapRequiredTerms);
        const preferredTerms = parseList(gapPreferredTerms);
        const allowedLicenses = parseList(gapAllowedLicenses);
        if (!title || !description || !requiredTerms.length) {
            setMessage("Enter a title, description, and at least one required capability term.");
            return;
        }
        let maxSizeBytes = null;
        const sizeText = gapMaxSizeMb.trim();
        if (sizeText) {
            const megabytes = Number(sizeText);
            if (!Number.isFinite(megabytes) || megabytes < 0) {
                setMessage("Maximum size must be a non-negative number of megabytes.");
                return;
            }
            maxSizeBytes = Math.round(megabytes * 1024 * 1024);
        }
        const confirmed = window.confirm("Create explicit capability gap?\n\n" +
            `${title}\n` +
            `Required terms: ${requiredTerms.join(", ")}\n\n` +
            "This records an administrator-defined need only. Ava will not " +
            "infer a gap, select a candidate, approve content, download files, " +
            "install, activate, or execute anything.");
        if (!confirmed)
            return;
        const created = await mutate("/api/v1/admin/librarian/gaps", {
            title,
            description,
            required_terms: requiredTerms,
            preferred_terms: preferredTerms,
            allowed_licenses: allowedLicenses,
            max_size_bytes: maxSizeBytes,
            priority: gapPriority,
        }, "Capability gap recorded. No candidate was selected.");
        if (created) {
            setGapTitle("");
            setGapDescription("");
            setGapRequiredTerms("");
            setGapPreferredTerms("");
            setGapAllowedLicenses("");
            setGapMaxSizeMb("");
            setGapPriority("normal");
        }
    }
    async function rankGap(gap) {
        const confirmed = window.confirm("Rank current recommendation metadata?\n\n" +
            `${gap.title}\n\n` +
            "The ranking is advisory only. Ava will not select or approve a " +
            "candidate, fetch pack files, download, install, activate, or execute anything.");
        if (!confirmed)
            return;
        try {
            setBusy(true);
            setMessage("");
            setWarning("");
            const response = await fetch(`${API_BASE}/api/v1/admin/librarian/gaps/${encodeURIComponent(gap.gap_id)}/rank`, {
                method: "POST",
                credentials: "include",
                headers,
                body: JSON.stringify({}),
            });
            const data = await safeJson(response);
            if (!response.ok) {
                setMessage(`❌ ${detailFrom(data, "Capability ranking failed.")}`);
                return;
            }
            setRankingsByGapId((current) => ({
                ...current,
                [gap.gap_id]: data,
            }));
            setMessage(data?.message ||
                "Current metadata ranked. No candidate was selected.");
        }
        catch (error) {
            setMessage(error?.message || "Capability ranking request failed.");
        }
        finally {
            setBusy(false);
        }
    }
    async function changeGapStatus(gap, action) {
        let note = null;
        if (action !== "reopen") {
            const entered = window.prompt(action === "resolve"
                ? "Resolution note:"
                : "Dismissal note:", "");
            if (entered === null)
                return;
            note = entered.trim() || null;
        }
        const label = action === "reopen"
            ? "Reopen"
            : action === "resolve"
                ? "Resolve"
                : "Dismiss";
        const confirmed = window.confirm(`${label} capability gap?\n\n${gap.title}`);
        if (!confirmed)
            return;
        const changed = await mutate(`/api/v1/admin/librarian/gaps/${encodeURIComponent(gap.gap_id)}/${action}`, action === "reopen" ? {} : { note }, `Capability gap ${action}d.`);
        if (changed) {
            setRankingsByGapId((current) => {
                const next = { ...current };
                delete next[gap.gap_id];
                return next;
            });
        }
    }
    async function registerSource() {
        const cleanName = sourceName.trim();
        const cleanPublisher = publisher.trim();
        const cleanUrl = baseUrl.trim();
        if (!cleanName || !cleanPublisher || !cleanUrl) {
            setMessage("Enter a source name, publisher, and HTTPS base URL.");
            return;
        }
        const created = await mutate("/api/v1/admin/librarian/sources", {
            name: cleanName,
            publisher: cleanPublisher,
            base_url: cleanUrl,
        }, "Approved source registered. Nothing was downloaded.");
        if (created) {
            setSourceName("");
            setPublisher("");
            setBaseUrl("");
        }
    }
    async function scanApprovedSources() {
        const confirmed = window.confirm("Scan all enabled approved sources?\n\n" +
            "Only the fixed, bounded JSON metadata index will be read. " +
            "No knowledge packs, files, or executables will be downloaded.");
        if (!confirmed)
            return;
        await mutate("/api/v1/admin/librarian/scan", {}, "Approved-source metadata scan completed. Nothing was downloaded or installed.");
    }
    async function toggleSource(source) {
        const action = source.enabled ? "disable" : "enable";
        const confirmed = window.confirm(`${source.enabled ? "Disable" : "Enable"} discovery source?\n\n` +
            `${source.publisher} — ${source.name}\n` +
            `${source.base_url}\n\n` +
            "This changes discovery eligibility only.");
        if (!confirmed)
            return;
        await mutate(`/api/v1/admin/librarian/sources/${encodeURIComponent(source.source_id)}/${action}`, {}, `Source ${action}d.`);
    }
    async function pinSigningKey(source) {
        const keyId = window.prompt("Publisher signing-key ID:", source.signing_key?.key_id || "");
        if (keyId === null)
            return;
        const cleanKeyId = keyId.trim();
        if (!cleanKeyId) {
            setMessage("Enter the publisher signing-key ID.");
            return;
        }
        const publicKey = window.prompt("Paste the publisher's base64 Ed25519 public key.\n\n" +
            "The key must decode to exactly 32 bytes.", "");
        if (publicKey === null)
            return;
        const cleanPublicKey = publicKey.trim();
        if (!cleanPublicKey) {
            setMessage("Enter the publisher Ed25519 public key.");
            return;
        }
        const confirmed = window.confirm(`${source.signing_key?.configured ? "Rotate" : "Pin"} publisher signing key?\n\n` +
            `${source.publisher} — ${source.name}\n` +
            `Key ID: ${cleanKeyId}\n\n` +
            "Changing a pinned key makes earlier successful manifest " +
            "verifications stale. No knowledge pack files will be fetched.");
        if (!confirmed)
            return;
        await mutate(`/api/v1/admin/librarian/sources/${encodeURIComponent(source.source_id)}/signing-key`, {
            key_id: cleanKeyId,
            public_key_b64: cleanPublicKey,
        }, "Publisher Ed25519 signing key pinned.");
    }
    async function verifyManifest(recommendation) {
        if (recommendation.status !== "approved") {
            setMessage("Approve the recommendation before verifying its manifest.");
            return;
        }
        const confirmed = window.confirm("Verify signed knowledge manifest?\n\n" +
            `${recommendation.title}\n` +
            `Publisher: ${recommendation.publisher}\n` +
            `Version: ${recommendation.version}\n` +
            `Manifest: ${recommendation.manifest_path}\n\n` +
            "AVAAIO will fetch only the bounded JSON manifest and verify " +
            "its identity, declared files, hashes, compatibility metadata, " +
            "and Ed25519 signature. Pack files will not be downloaded, " +
            "installed, activated, or executed.");
        if (!confirmed)
            return;
        await mutate(`/api/v1/admin/librarian/recommendations/${encodeURIComponent(recommendation.recommendation_id)}/verify-manifest`, {}, "Signed manifest verified. No pack files were downloaded.");
    }
    async function reviewRecommendation(recommendation, decision) {
        const approving = decision === "approve";
        const confirmed = window.confirm(`${approving ? "Approve" : "Reject"} knowledge recommendation?\n\n` +
            `${recommendation.title}\n` +
            `Publisher: ${recommendation.publisher}\n` +
            `Version: ${recommendation.version}\n\n` +
            (approving
                ? "Approval records eligibility only. Nothing will be downloaded or installed."
                : "The recommendation will be marked rejected."));
        if (!confirmed)
            return;
        const note = window.prompt("Optional administrator review note:", "");
        if (note === null)
            return;
        await mutate(`/api/v1/admin/librarian/recommendations/${encodeURIComponent(recommendation.recommendation_id)}/${decision}`, {
            note: note.trim() || null,
        }, `Recommendation ${decision}d.`);
    }
    const mode = String(status?.mode || "discovery_only").replace(/_/g, " ");
    return (_jsxs(AppSectionCard, { children: [_jsx("div", { style: styles.title, children: "AVAAIO Local Librarian" }), _jsx("div", { style: styles.note, children: "Explicit capability gaps, deterministic advisory ranking, approval-gated discovery, and signed-manifest verification. Ava cannot automatically infer needs, select candidates, download, install, activate, execute, or approve knowledge packs." }), _jsxs("div", { style: styles.badges, children: [_jsxs("div", { style: styles.primaryBadge, children: ["MODE: ", mode] }), _jsxs("div", { style: styles.badge, children: ["PENDING: ", status?.recommendations?.pending ?? 0] }), _jsxs("div", { style: styles.badge, children: ["SOURCES: ", status?.sources?.enabled ?? 0, "/", status?.sources?.total ?? 0] }), _jsxs("div", { style: styles.badge, children: ["VERIFIED MANIFESTS:", " ", status?.manifest_verification?.verified ?? 0] }), _jsxs("div", { style: styles.badge, children: ["OPEN GAPS: ", status?.capability_gaps?.open ?? 0] })] }), _jsx("div", { style: styles.securityBox, children: "Automatic gap inference: OFF \u2022 Automatic selection: OFF \u2022 Automatic approval: OFF \u2022 Automatic downloads: OFF \u2022 Pack-file fetching: OFF \u2022 Installation: OFF \u2022 Activation: OFF \u2022 Execution: OFF \u2022 Elevated administrator review: REQUIRED" }), _jsx("div", { style: styles.subTitle, children: "Explicit Capability Gaps" }), _jsx("div", { style: styles.note, children: "Define exactly what Ava is missing. Required terms are hard eligibility gates. Compatibility remains unscored until AVAAIO has a trustworthy runtime-version signal." }), _jsx("input", { value: gapTitle, onChange: (event) => setGapTitle(event.target.value), placeholder: "Capability-gap title", style: styles.inputSpaced }), _jsx("textarea", { value: gapDescription, onChange: (event) => setGapDescription(event.target.value), placeholder: "Describe the missing capability and why it is needed", style: styles.textarea }), _jsx("input", { value: gapRequiredTerms, onChange: (event) => setGapRequiredTerms(event.target.value), placeholder: "Required terms, comma separated \u2014 e.g. networking, DNS", style: styles.inputSpaced }), _jsx("input", { value: gapPreferredTerms, onChange: (event) => setGapPreferredTerms(event.target.value), placeholder: "Preferred terms, comma separated \u2014 optional", style: styles.inputSpaced }), _jsx("input", { value: gapAllowedLicenses, onChange: (event) => setGapAllowedLicenses(event.target.value), placeholder: "Allowed licenses, comma separated \u2014 optional", style: styles.inputSpaced }), _jsxs("div", { style: styles.formGrid, children: [_jsx("input", { value: gapMaxSizeMb, onChange: (event) => setGapMaxSizeMb(event.target.value), placeholder: "Maximum size in MB \u2014 optional", inputMode: "decimal", style: styles.input }), _jsxs("select", { value: gapPriority, onChange: (event) => setGapPriority(event.target.value), style: styles.input, children: [_jsx("option", { value: "low", children: "Low priority" }), _jsx("option", { value: "normal", children: "Normal priority" }), _jsx("option", { value: "high", children: "High priority" }), _jsx("option", { value: "critical", children: "Critical priority" })] })] }), _jsx("div", { style: styles.actions, children: _jsx("button", { type: "button", style: pageStyles.button, disabled: busy, onClick: () => void createGap(), children: busy ? "Working..." : "Create Capability Gap" }) }), _jsx("div", { style: styles.gapList, children: gaps.length ? (gaps.map((gap) => {
                    const ranking = rankingsByGapId[gap.gap_id];
                    return (_jsxs("div", { style: {
                            ...styles.item,
                            border: gap.status === "open"
                                ? "1px solid rgba(103,232,249,0.28)"
                                : gap.status === "resolved"
                                    ? "1px solid rgba(110,255,170,0.28)"
                                    : "1px solid rgba(255,205,100,0.26)",
                        }, children: [_jsx("div", { style: styles.itemTitle, children: gap.title }), _jsxs("div", { style: styles.meta, children: ["Priority: ", gap.priority.toUpperCase(), " \u2022 Status:", " ", gap.status.toUpperCase(), " \u2022 Explicit administrator definition"] }), _jsx("div", { style: styles.note, children: gap.description }), _jsxs("div", { style: styles.reason, children: ["Required: ", gap.required_terms.join(", ")] }), gap.preferred_terms.length ? (_jsxs("div", { style: styles.meta, children: ["Preferred: ", gap.preferred_terms.join(", ")] })) : null, gap.allowed_licenses.length ? (_jsxs("div", { style: styles.meta, children: ["Allowed licenses:", " ", gap.allowed_licenses.join(", ")] })) : null, _jsxs("div", { style: styles.meta, children: ["Maximum size:", " ", gap.max_size_bytes == null
                                        ? "No additional limit"
                                        : formatBytes(gap.max_size_bytes)] }), gap.resolution_note ? (_jsxs("div", { style: styles.note, children: ["Administrator note: ", gap.resolution_note] })) : null, _jsxs("div", { style: styles.breakable, children: ["Gap fingerprint: ", gap.fingerprint_sha256] }), _jsx("div", { style: styles.actions, children: gap.status === "open" ? (_jsxs(_Fragment, { children: [_jsx("button", { type: "button", style: {
                                                ...pageStyles.button,
                                                border: "1px solid rgba(103,232,249,0.48)",
                                            }, disabled: busy, onClick: () => void rankGap(gap), children: "Rank Current Candidates" }), _jsx("button", { type: "button", style: pageStyles.button, disabled: busy, onClick: () => void changeGapStatus(gap, "resolve"), children: "Mark Resolved" }), _jsx("button", { type: "button", style: pageStyles.button, disabled: busy, onClick: () => void changeGapStatus(gap, "dismiss"), children: "Dismiss" })] })) : (_jsx("button", { type: "button", style: pageStyles.button, disabled: busy, onClick: () => void changeGapStatus(gap, "reopen"), children: "Reopen Gap" })) }), ranking ? (_jsxs("div", { style: styles.rankingBox, children: [_jsx("div", { style: styles.itemTitle, children: "Advisory Candidate Ranking" }), _jsxs("div", { style: styles.meta, children: ["Policy: ", ranking.ranking_policy, " \u2022 Candidates:", " ", ranking.candidate_count, " \u2022 Eligible:", " ", ranking.eligible_count, " \u2022 Ineligible:", " ", ranking.ineligible_count] }), _jsx("div", { style: styles.securityMini, children: "Metadata only \u2022 Not selected \u2022 Not approved \u2022 Not downloaded \u2022 Not installed \u2022 Not activated \u2022 Not executed" }), ranking.candidates.length ? (ranking.candidates.map((candidate) => (_jsxs("div", { style: styles.candidate, children: [_jsxs("div", { style: styles.itemTitle, children: ["#", candidate.rank, " ", candidate.candidate.title ||
                                                        candidate.recommendation_id] }), _jsxs("div", { style: styles.score, children: ["Score: ", candidate.score.toFixed(2), " / 100 \u2022", " ", candidate.eligible
                                                        ? "ELIGIBLE"
                                                        : "INELIGIBLE"] }), _jsxs("div", { style: styles.meta, children: [candidate.candidate.publisher ||
                                                        "Unknown publisher", " ", "\u2022 Version", " ", candidate.candidate.version || "unknown", " \u2022", " ", formatBytes(candidate.candidate.size_bytes)] }), _jsxs("div", { style: styles.meta, children: ["Relevance:", " ", candidate.score_breakdown.relevance, " \u2022 Trust:", " ", candidate.score_breakdown.trust, " \u2022 License:", " ", candidate.score_breakdown.license, " \u2022 Efficiency:", " ", candidate.score_breakdown.efficiency, " \u2022 Freshness:", " ", candidate.score_breakdown.freshness, " \u2022 Compatibility: NOT SCORED"] }), candidate.matched_required_terms.length ? (_jsxs("div", { style: styles.meta, children: ["Matched required terms:", " ", candidate.matched_required_terms.join(", ")] })) : null, candidate.missing_required_terms.length ? (_jsxs("div", { style: styles.warningInline, children: ["Missing required terms:", " ", candidate.missing_required_terms.join(", ")] })) : null, candidate.eligibility_reasons.length ? (_jsxs("div", { style: styles.warningInline, children: ["Ineligible because:", " ", candidate.eligibility_reasons
                                                        .map((reason) => reason.replace(/_/g, " "))
                                                        .join(", ")] })) : null, candidate.explanation.length ? (_jsx("div", { style: styles.note, children: candidate.explanation.join(" ") })) : null] }, candidate.recommendation_id)))) : (_jsx("div", { style: styles.note, children: "No current recommendation metadata is available to rank." }))] })) : null] }, gap.gap_id));
                })) : (_jsx("div", { style: styles.note, children: "No capability gaps have been explicitly defined." })) }), _jsx("div", { style: styles.subTitle, children: "Register Approved Source" }), _jsx("input", { value: sourceName, onChange: (event) => setSourceName(event.target.value), placeholder: "Source name", style: styles.input }), _jsx("input", { value: publisher, onChange: (event) => setPublisher(event.target.value), placeholder: "Publisher", style: styles.inputSpaced }), _jsx("input", { value: baseUrl, onChange: (event) => setBaseUrl(event.target.value), placeholder: "https://trusted-publisher.example/packs", inputMode: "url", style: styles.inputSpaced }), _jsxs("div", { style: styles.actions, children: [_jsx("button", { type: "button", style: pageStyles.button, disabled: busy, onClick: () => void registerSource(), children: busy ? "Saving..." : "Register Approved Source" }), _jsx("button", { type: "button", style: {
                            ...pageStyles.button,
                            border: "1px solid rgba(110,255,170,0.52)",
                        }, disabled: busy || !sources.some((source) => source.enabled), onClick: () => void scanApprovedSources(), title: sources.some((source) => source.enabled)
                            ? "Scan metadata from enabled approved sources"
                            : "Register and enable an approved source first", children: busy ? "Working..." : "Scan Approved Sources" }), _jsx("button", { type: "button", style: pageStyles.button, disabled: busy, onClick: () => void loadLibrarian(), children: "Refresh Librarian" })] }), _jsx("div", { style: styles.subTitle, children: "Approved Sources" }), _jsx("div", { style: styles.list, children: sources.length ? (sources.map((source) => (_jsxs("div", { style: styles.item, children: [_jsxs("div", { style: styles.itemTitle, children: [source.publisher, " \u2014 ", source.name] }), _jsxs("div", { style: styles.meta, children: [source.hostname, " \u2022", " ", source.enabled ? "ENABLED" : "DISABLED"] }), _jsx("div", { style: styles.breakable, children: source.base_url }), _jsxs("div", { style: styles.meta, children: ["Signing key:", " ", source.signing_key?.configured
                                    ? source.signing_key.key_id || "PINNED"
                                    : "NOT PINNED"] }), source.signing_key
                            ?.public_key_fingerprint_sha256 ? (_jsxs("div", { style: styles.breakable, children: ["Ed25519 fingerprint:", " ", source.signing_key
                                    .public_key_fingerprint_sha256] })) : null, _jsxs("div", { style: styles.actions, children: [_jsx("button", { type: "button", style: pageStyles.button, disabled: busy, onClick: () => void toggleSource(source), children: source.enabled
                                        ? "Disable Discovery"
                                        : "Enable Discovery" }), _jsx("button", { type: "button", style: pageStyles.button, disabled: busy, onClick: () => void pinSigningKey(source), children: source.signing_key?.configured
                                        ? "Rotate Signing Key"
                                        : "Pin Signing Key" })] })] }, source.source_id)))) : (_jsx("div", { style: styles.note, children: "No approved sources have been registered." })) }), _jsx("div", { style: styles.subTitle, children: "Knowledge Recommendations" }), _jsx("div", { style: styles.recommendationList, children: recommendations.length ? (recommendations.map((recommendation) => (_jsxs("div", { style: {
                        ...styles.item,
                        border: recommendation.status === "approved"
                            ? "1px solid rgba(110,255,170,0.32)"
                            : recommendation.status === "rejected"
                                ? "1px solid rgba(255,140,140,0.32)"
                                : "1px solid rgba(103,232,249,0.20)",
                    }, children: [_jsx("div", { style: styles.itemTitle, children: recommendation.title }), _jsxs("div", { style: styles.meta, children: [recommendation.publisher, " \u2022 Version", " ", recommendation.version, " \u2022", " ", formatBytes(recommendation.size_bytes)] }), _jsxs("div", { style: styles.meta, children: ["License: ", recommendation.license_name, " \u2022 Signature:", " ", recommendation.signature_status, " \u2022 Status:", " ", recommendation.status.toUpperCase()] }), _jsx("div", { style: styles.note, children: recommendation.description ||
                                "No description provided." }), _jsxs("div", { style: styles.reason, children: ["Why Ava recommends it:", " ", recommendation.recommendation_reason] }), _jsxs("div", { style: styles.breakable, children: ["Source:", " ", recommendation.source_name ||
                                    recommendation.source_hostname ||
                                    recommendation.source_id, _jsx("br", {}), "Manifest: ", recommendation.manifest_path, _jsx("br", {}), "Package SHA-256: ", recommendation.sha256] }), _jsxs("div", { style: {
                                ...styles.verificationBox,
                                border: recommendation.manifest_verification?.status ===
                                    "verified"
                                    ? "1px solid rgba(110,255,170,0.32)"
                                    : recommendation.manifest_verification
                                        ?.status === "failed"
                                        ? "1px solid rgba(255,120,120,0.34)"
                                        : recommendation.manifest_verification
                                            ?.status === "stale"
                                            ? "1px solid rgba(255,205,100,0.34)"
                                            : "1px solid rgba(103,232,249,0.18)",
                            }, children: [_jsxs("div", { style: styles.meta, children: ["Manifest verification:", " ", (recommendation.manifest_verification?.status ||
                                            "not_verified")
                                            .replace(/_/g, " ")
                                            .toUpperCase()] }), _jsxs("div", { style: styles.meta, children: ["Signing key:", " ", recommendation.signing_key?.configured
                                            ? recommendation.signing_key.key_id ||
                                                "PINNED"
                                            : "NOT PINNED"] }), recommendation.manifest_verification
                                    ?.manifest_sha256 ? (_jsxs("div", { style: styles.breakable, children: ["Manifest SHA-256:", " ", recommendation.manifest_verification
                                            .manifest_sha256] })) : null, recommendation.manifest_verification
                                    ?.file_count != null ? (_jsxs("div", { style: styles.meta, children: ["Declared files:", " ", recommendation.manifest_verification
                                            .file_count, " ", "\u2022 Declared bytes:", " ", formatBytes(recommendation.manifest_verification
                                            .total_file_bytes)] })) : null, recommendation.manifest_verification
                                    ?.verified_at ? (_jsxs("div", { style: styles.meta, children: ["Last checked:", " ", recommendation.manifest_verification
                                            .verified_at] })) : null, recommendation.manifest_verification
                                    ?.stale_reasons?.length ? (_jsxs("div", { style: styles.note, children: ["Stale because:", " ", recommendation.manifest_verification.stale_reasons
                                            .map((reason) => reason.replace(/_/g, " "))
                                            .join(", ")] })) : null, recommendation.manifest_verification?.error ? (_jsxs("div", { style: styles.note, children: ["Verification error:", " ", recommendation.manifest_verification
                                            .error] })) : null] }), recommendation.source_updated_at ? (_jsxs("div", { style: styles.meta, children: ["Publisher updated:", " ", recommendation.source_updated_at] })) : null, recommendation.review_note ? (_jsxs("div", { style: styles.note, children: ["Review note: ", recommendation.review_note] })) : null, recommendation.provenance &&
                            Object.keys(recommendation.provenance).length ? (_jsxs("details", { style: styles.provenance, children: [_jsx("summary", { children: "Provenance details" }), _jsx("pre", { style: styles.pre, children: JSON.stringify(recommendation.provenance, null, 2) })] })) : null, recommendation.status === "pending" ? (_jsxs("div", { style: styles.actions, children: [_jsx("button", { type: "button", style: pageStyles.button, disabled: busy, onClick: () => void reviewRecommendation(recommendation, "approve"), children: "Approve Eligibility" }), _jsx("button", { type: "button", style: pageStyles.button, disabled: busy, onClick: () => void reviewRecommendation(recommendation, "reject"), children: "Reject" })] })) : null, recommendation.status === "approved" ? (_jsx("div", { style: styles.actions, children: _jsx("button", { type: "button", style: {
                                    ...pageStyles.button,
                                    border: "1px solid rgba(110,255,170,0.52)",
                                }, disabled: busy ||
                                    recommendation.source_enabled === false ||
                                    !recommendation.signing_key?.configured, onClick: () => void verifyManifest(recommendation), title: recommendation.source_enabled === false
                                    ? "Enable the approved source first"
                                    : !recommendation.signing_key
                                        ?.configured
                                        ? "Pin the publisher signing key first"
                                        : "Fetch and verify only the signed JSON manifest", children: recommendation.manifest_verification
                                    ?.status === "verified"
                                    ? "Verify Manifest Again"
                                    : "Verify Signed Manifest" }) })) : null] }, recommendation.recommendation_id)))) : (_jsx("div", { style: styles.note, children: "No knowledge recommendations are waiting for review." })) }), message ? _jsx("div", { style: styles.message, children: message }) : null, warning ? _jsx("div", { style: styles.warning, children: warning }) : null] }));
}
const styles = {
    title: {
        fontSize: "18px",
        fontWeight: 950,
        color: "#bffcff",
        letterSpacing: "0.055em",
        textTransform: "uppercase",
        marginBottom: "14px",
    },
    subTitle: {
        marginTop: "22px",
        marginBottom: "12px",
        color: "#67f7ff",
        fontSize: "13px",
        fontWeight: 900,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
    },
    note: {
        color: "rgba(235,252,255,0.82)",
        lineHeight: 1.65,
        fontSize: "14px",
        marginTop: "8px",
    },
    badges: {
        display: "flex",
        gap: "9px",
        flexWrap: "wrap",
        margin: "16px 0 12px",
    },
    primaryBadge: {
        padding: "9px 12px",
        borderRadius: "13px",
        background: "rgba(34,211,238,0.18)",
        border: "1px solid rgba(103,232,249,0.34)",
        fontSize: "12px",
        fontWeight: 900,
        textTransform: "uppercase",
    },
    badge: {
        padding: "9px 12px",
        borderRadius: "13px",
        background: "rgba(255,255,255,0.05)",
        border: "1px solid rgba(255,255,255,0.12)",
        fontSize: "12px",
        fontWeight: 850,
    },
    securityBox: {
        padding: "12px 14px",
        borderRadius: "14px",
        border: "1px solid rgba(110,255,170,0.28)",
        background: "rgba(20,84,55,0.18)",
        color: "#cffff0",
        fontSize: "13px",
        lineHeight: 1.6,
    },
    input: {
        width: "100%",
        minHeight: "50px",
        boxSizing: "border-box",
        borderRadius: "14px",
        border: "1px solid rgba(103,232,249,0.18)",
        background: "rgba(3,10,20,0.86)",
        color: "#fff",
        padding: "0 15px",
        fontSize: "14px",
    },
    inputSpaced: {
        width: "100%",
        minHeight: "50px",
        boxSizing: "border-box",
        borderRadius: "14px",
        border: "1px solid rgba(103,232,249,0.18)",
        background: "rgba(3,10,20,0.86)",
        color: "#fff",
        padding: "0 15px",
        fontSize: "14px",
        marginTop: "10px",
    },
    textarea: {
        width: "100%",
        minHeight: "110px",
        boxSizing: "border-box",
        borderRadius: "14px",
        border: "1px solid rgba(103,232,249,0.18)",
        background: "rgba(3,10,20,0.86)",
        color: "#fff",
        padding: "14px 15px",
        fontSize: "14px",
        lineHeight: 1.55,
        marginTop: "10px",
        resize: "vertical",
    },
    formGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
        gap: "10px",
        marginTop: "10px",
    },
    actions: {
        display: "flex",
        gap: "10px",
        flexWrap: "wrap",
        marginTop: "12px",
    },
    list: {
        display: "flex",
        flexDirection: "column",
        gap: "9px",
        maxHeight: "300px",
        overflowY: "auto",
    },
    recommendationList: {
        display: "flex",
        flexDirection: "column",
        gap: "9px",
        maxHeight: "540px",
        overflowY: "auto",
    },
    gapList: {
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        maxHeight: "760px",
        overflowY: "auto",
        marginTop: "14px",
    },
    rankingBox: {
        marginTop: "13px",
        padding: "12px",
        borderRadius: "13px",
        border: "1px solid rgba(103,232,249,0.24)",
        background: "rgba(2,12,20,0.62)",
    },
    candidate: {
        marginTop: "10px",
        padding: "11px",
        borderRadius: "12px",
        border: "1px solid rgba(255,255,255,0.10)",
        background: "rgba(255,255,255,0.035)",
    },
    score: {
        marginTop: "6px",
        color: "#bffcff",
        fontSize: "13px",
        fontWeight: 850,
    },
    securityMini: {
        marginTop: "9px",
        padding: "8px 10px",
        borderRadius: "10px",
        border: "1px solid rgba(110,255,170,0.22)",
        color: "#cffff0",
        background: "rgba(20,84,55,0.13)",
        fontSize: "11px",
        lineHeight: 1.5,
    },
    warningInline: {
        marginTop: "7px",
        color: "#ffcf9d",
        fontSize: "12px",
        lineHeight: 1.5,
    },
    item: {
        display: "flex",
        flexDirection: "column",
        padding: "13px",
        borderRadius: "14px",
        border: "1px solid rgba(103,232,249,0.12)",
        background: "rgba(255,255,255,0.035)",
    },
    itemTitle: {
        color: "#fff",
        fontSize: "14px",
        fontWeight: 750,
    },
    meta: {
        marginTop: "5px",
        color: "rgba(255,255,255,0.7)",
        fontSize: "12px",
        lineHeight: 1.5,
    },
    breakable: {
        marginTop: "7px",
        color: "rgba(255,255,255,0.68)",
        fontSize: "12px",
        lineHeight: 1.55,
        wordBreak: "break-all",
    },
    reason: {
        marginTop: "9px",
        color: "#bffcff",
        fontSize: "13px",
        lineHeight: 1.6,
    },
    verificationBox: {
        marginTop: "11px",
        padding: "10px 12px",
        borderRadius: "12px",
        background: "rgba(2,12,20,0.54)",
    },
    provenance: {
        marginTop: "10px",
        color: "#bffcff",
        fontSize: "12px",
    },
    pre: {
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        color: "rgba(255,255,255,0.76)",
        fontSize: "11px",
        lineHeight: 1.5,
    },
    message: {
        marginTop: "14px",
        padding: "11px 13px",
        borderRadius: "13px",
        background: "rgba(34,211,238,0.07)",
        border: "1px solid rgba(103,232,249,0.18)",
        color: "#bffcff",
    },
    warning: {
        marginTop: "14px",
        padding: "11px 13px",
        borderRadius: "13px",
        background: "rgba(255,80,80,0.08)",
        border: "1px solid rgba(255,120,120,0.25)",
        color: "#ffb3b3",
    },
};
